<template>
  <div class="farm-data-preview">
    <div v-if="!data || data.length === 0" class="no-data">
      <p>No farm data detected in the uploaded file</p>
    </div>
    
    <div v-else class="data-table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>PIC</th>
            <th>Farm Name</th>
            <th>Region</th>
            <th>Owner</th>
            <th>Sustainability</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(farm, index) in data" :key="index">
            <td>
              <input 
                type="text" 
                v-model="farm.PIC" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.PIC }"
              >
              <div v-if="validationErrors[index]?.PIC" class="error-message">
                {{ validationErrors[index].PIC }}
              </div>
            </td>
            <td>
              <input 
                type="text" 
                v-model="farm.FarmName" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.FarmName }"
              >
              <div v-if="validationErrors[index]?.FarmName" class="error-message">
                {{ validationErrors[index].FarmName }}
              </div>
            </td>
            <td>
              <input 
                type="text" 
                v-model="farm.Region" 
                class="table-input"
              >
            </td>
            <td>
              <input 
                type="text" 
                v-model="farm.OwnerName" 
                class="table-input"
              >
            </td>
            <td>
              <select v-model="farm.SustainaWool" class="table-select">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </td>
            <td class="actions-cell">
              <button class="btn-icon" @click="expandFarm(index)" :title="farm.expanded ? 'Collapse' : 'Expand'">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline v-if="!farm.expanded" points="6 9 12 15 18 9"></polyline>
                  <polyline v-else points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
              <button class="btn-icon delete" @click="removeFarm(index)" title="Remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Expanded Farm Details -->
      <div v-for="(farm, index) in data" :key="`details-${index}`" class="expanded-details" v-show="farm.expanded">
        <div class="details-grid">
          <div class="detail-group">
            <label>State</label>
            <input type="text" v-model="farm.State" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>LGA</label>
            <input type="text" v-model="farm.LGA" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Latitude</label>
            <input type="text" v-model="farm.Lat" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Longitude</label>
            <input type="text" v-model="farm.Lon" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Carbon Score</label>
            <input type="number" v-model.number="farm.CarbonScore" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Water Use</label>
            <input type="text" v-model="farm.WaterUse" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Land Impact</label>
            <input type="text" v-model="farm.LandImpact" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Biodiversity Practices</label>
            <textarea v-model="farm.BiodiversityPractices" class="detail-textarea"></textarea>
          </div>
        </div>
        
        <div class="connected-data">
          <h4>Connected Wool Lines</h4>
          <div v-if="farm.connectedLines && farm.connectedLines.length > 0" class="connected-items">
            <div v-for="(line, lineIndex) in farm.connectedLines" :key="`line-${lineIndex}`" class="connected-item">
              <span>{{ line.LineID }}</span>
              <span class="item-detail">{{ line.Description || 'No description' }}</span>
            </div>
          </div>
          <div v-else class="no-connected-data">
            No connected wool lines detected
          </div>
        </div>
      </div>
    </div>
    
    <div class="add-item-row">
      <button class="btn-add" @click="addNewFarm">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Add Farm
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      validationErrors: [],
      farmTemplate: {
        PIC: '',
        FarmName: '',
        Region: '',
        OwnerName: '',
        State: 'NSW',
        LGA: '',
        SustainaWool: false,
        Lat: '',
        Lon: '',
        CarbonScore: 0,
        WaterUse: '',
        LandImpact: '',
        BiodiversityPractices: '',
        expanded: false,
        connectedLines: []
      }
    };
  },
  
  watch: {
    data: {
      handler(newData) {
        // Initialize expanded property if not present
        if (newData) {
          newData.forEach(farm => {
            if (farm.expanded === undefined) {
              this.$set(farm, 'expanded', false);
            }
          });
        }
        
        // Reset validation errors when data changes
        this.validationErrors = Array(newData?.length || 0).fill(null);
        
        // Emit updated data
        this.$emit('update', newData);
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    expandFarm(index) {
      if (this.data[index]) {
        this.$set(this.data[index], 'expanded', !this.data[index].expanded);
      }
    },
    
    removeFarm(index) {
      if (confirm('Are you sure you want to remove this farm?')) {
        const updatedData = [...this.data];
        updatedData.splice(index, 1);
        this.$emit('update', updatedData);
      }
    },
    
    addNewFarm() {
      const updatedData = [...this.data];
      updatedData.push({...this.farmTemplate});
      this.$emit('update', updatedData);
    },
    
    validateFarms() {
      let isValid = true;
      const errors = Array(this.data.length).fill(null).map(() => ({}));
      
      this.data.forEach((farm, index) => {
        // Validate PIC
        if (!farm.PIC) {
          errors[index].PIC = 'PIC is required';
          isValid = false;
        } else if (!/^[A-Z]{3}[A-Z0-9]{4,7}$/.test(farm.PIC)) {
          errors[index].PIC = 'Invalid PIC format';
          isValid = false;
        }
        
        // Validate Farm Name
        if (!farm.FarmName) {
          errors[index].FarmName = 'Farm name is required';
          isValid = false;
        }
      });
      
      this.validationErrors = errors;
      return isValid;
    }
  }
};
</script>

<style scoped>
.farm-data-preview {
  margin-bottom: 24px;
}

.no-data {
  padding: 40px;
  text-align: center;
  background-color: #f9fafb;
  border-radius: 6px;
  color: #6b7280;
}

.data-table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background-color: #f9fafb;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.table-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.table-input:focus {
  border-color: #3A895F;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 137, 95, 0.2);
}

.table-input.has-error {
  border-color: #ef4444;
}

.error-message {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.table-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
}

.actions-cell {
  white-space: nowrap;
  width: 80px;
}

.btn-icon {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  margin-right: 4px;
}

.btn-icon:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.btn-icon.delete:hover {
  background-color: #fee2e2;
  color: #b91c1c;
}

.expanded-details {
  background-color: #f9fafb;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 0 0 6px 6px;
  border: 1px solid #e5e7eb;
  border-top: none;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.detail-group {
  display: flex;
  flex-direction: column;
}

.detail-group label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
}

.detail-input {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.detail-textarea {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
}

.connected-data {
  margin-top: 16px;
}

.connected-data h4 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
}

.connected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.connected-item {
  background-color: #e5e7eb;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  display: flex;
  align-items: center;
}

.item-detail {
  color: #6b7280;
  font-size: 12px;
  margin-left: 6px;
  padding-left: 6px;
  border-left: 1px solid #d1d5db;
}

.no-connected-data {
  color: #6b7280;
  font-size: 13px;
  font-style: italic;
}

.add-item-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: white;
  border: 1px dashed #d1d5db;
  color: #3A895F;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add:hover {
  background-color: #f9fafb;
  border-color: #3A895F;
}
</style>
